import { useConvictionProjectionInterest } from '../internal/conviction-projection-interest.js';
/**
 * T15007 · Wildcard-cover detection — consumer-side seam.
 *
 * Spec 05 §579-581 property (3): a `domain` hook whose `domainPins`
 * includes a wildcard (`morfi.market.*.trade`) implicitly satisfies any
 * per-entity `morfi.market.{X}.trade` request. The registry (T7100/T7102,
 * owned by `src/nats/subject-registry.ts`) implements the trie-layer
 * coverage check; `createHook` composes BOTH `domainPins` AND
 * `perEntitySubjects` into the wrapper so the trie can resolve coverage
 * without consumer involvement. The contract + worked example are
 * documented in [`docs/06-realtime-and-nats.md`](../../docs/06-realtime-and-nats.md).
 *
 * Property (3) is exercised by the list + detail co-mount case:
 *   transport.subscribe('morfi.market.*.trade', …)   → called ONCE (list)
 *   transport.subscribe('morfi.market.M1.trade', …)  → NEVER called
 *                                                      (wildcard covers)
 *
 * Test fixture: `tests/integration/wildcard-cover-no-flicker.test.tsx`.
 */
/**
 * T15001 · `createHook(spec, realtime?)` composition factory.
 *
 * Spec file 05 §519-558. Composes a stateless codegen-emitted
 * {@link HookSpec} with an optional hand-written {@link RealtimeSpec} into
 * a typed read hook. The returned function is the public surface consumers
 * call (e.g. `useGetMarket({ id: 'M1' })`).
 *
 * Behavioural contract:
 *
 *   (a) `const queryOpts = spec.entityKeys[spec.kind](args)` — kind
 *       discriminator selects detail | list | infinite from the
 *       codegen-emitted factory bag.
 *
 *   (b) STATIC FALLTHROUGH. If `realtime` is omitted, the returned hook
 *       calls `useQuery(queryOpts)` directly. No NATS work, no triple-ref,
 *       no `useRealtimeQuery`. This is the load-bearing fallthrough for
 *       hooks that don't subscribe to any stream — keep it dependency-
 *       free so a static page can render without bringing in the
 *       registry.
 *
 *   (c) REALTIME PATH. Otherwise compose through `useRealtimeQuery` with:
 *         - `queryKey`, `queryFn`           from the factory output
 *         - `domain`, `domainPins`          from `realtime`
 *         - `perEntitySubjects`             evaluated as `realtime.perEntitySubjects(args)`
 *         - `viewportGated`                 from `realtime`
 *         - `applyEvent`                    evaluated as `realtime.applyEvent?.(args)`
 *         - `onSignal`                      handled by T15002 (signal path)
 *         - `specName`                      from `spec.specName`
 *
 *       The outer-curry invocation `realtime.applyEvent?.(args)` happens at
 *       hook construction so the inner fn closes over args-derived state —
 *       T2002's `applyEventRef` stashes the inner fn so updated args yield
 *       updated closures without resubscribing.
 *
 *   Composition guard: `applyEvent` and `onSignal` cannot coexist on the
 *   same RealtimeSpec — the XOR union enforces this at the TYPE level. The
 *   `'applyEvent' in realtime` / `'onSignal' in realtime` checks below
 *   correctly narrow the variants.
 */
import { hashKey, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useRef } from 'react';
import { getSdkAuthStore } from '../../auth/state/auth-state.js';
import { registerRealtimeSpec } from '../../cache/persist/realtime-query-registry.js';
import { registerViewerSensitiveSpec } from '../../cache/persist/viewer-sensitive-query-registry.js';
import { useRenderTelemetryDev } from '../../devtool/hooks/useRenderTelemetryDev.js';
import { registerQuerySpec } from '../../devtool/registry/spec-registry.js';
import { useSdkNetworkScope, useSdkViewerScope } from '../../provider/SdkRuntimeContext.js';
import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import { QueryExecutionContext } from '../context/QueryExecutionContext.js';
import { useRealtimeQuery } from '../hooks/useRealtimeQuery.js';
import { hasNestedEntitySnapshots, materializeNestedEntityProjection, nestedEntityIds, projectionEntityQueryKey, recordOutcomeEntityDiagnostic, splitNestedEntityProjection } from '../internal/entity-projection.js';
import { hasActiveFetchOnceRealtimeOwner } from '../internal/fetch-once-realtime-policy.js';
import { getCachedQuery } from '../internal/get-cached-query.js';
import { makeGiftEntityApplyEvent, writeGiftEntityResponse } from '../internal/gift-entity.js';
import { makeCanonicalEntityApplyEvent, makeNormalizedApplyEvent, makeNormalizedOnEventQueryClient, splitRowsIntoEntities } from '../internal/normalized-entity.js';
import { writeNormalizedEntitySnapshot } from '../internal/normalized-entity-write.js';
import { stripPublicUserIdentityFields, upsertPublicUserEntity } from '../internal/public-user-entity.js';
import { refetchRealtimeQuery } from '../internal/refetchRealtimeQuery.js';
import { resolveEntityQueryKey } from '../internal/scoped-entity-store.js';
import { createSubscriberQueryFn } from '../internal/subscriber-query-fn.js';
import { filterTerminalRows, terminalAwareQueryCache } from '../internal/terminal-row-fence.js';
import { mergeViewerOverlayRow, recordViewerOverlayRecovery, splitViewerOverlayRow, viewerOverlaySubscriptionKeys } from '../internal/viewer-overlay.js';
import { isPollingSubject } from '../polling/coverage.js';
import { capturePollingResponse, consumePollingBatch as consumeBatchedListPoll, observePollingFetch } from '../polling/state.js';
import { useQueryPolling } from '../polling/useQueryPolling.js';
export function createHook(spec, realtime, extras) {
  // Module-level construction; the returned function is the public hook.
  // T15006 · specName plumbing invariant. Spec 05 L283-291's
  // `useRealtimeQuery` signature OMITS `specName`; spec 09 T2002 added
  // `specName: string` as a 7th required field used by telemetry (the
  // never-bound dev guard + subscript-thrash sampler). Resolution: the
  // wrapper requires `specName`, but the public `createHook` API does NOT
  // — the value is sourced from `spec.specName` inside this factory so
  // consumers never need to pass it themselves. An empty string here is a
  // codegen invariant violation: telemetry attribution would lose its key.
  if (spec.specName === '') {
    throw new Error('createHook: spec.specName is empty — every codegen-emitted HookSpec ' + 'MUST carry a non-empty specName for telemetry attribution ' + '(never-bound guard, subscript-thrash sampler).');
  }
  // Persist-exclusion: register realtime single/detail queries' key-prefix
  // so the flush never writes them to the persisted cache. No-op for
  // non-realtime specs (the registry gates on the spec's `meta.realtime`).
  registerRealtimeSpec(spec);
  registerViewerSensitiveSpec(spec);
  // Register with the devtool spec dump at module-load time. We capture
  // the *static* realtime shape: domain wildcards (`domainPins`) plus the
  // dispatch kind (`applyEvent` / `onSignal` / pure-invalidate). The
  // per-entity subject builder is a function of args, so we cannot
  // introspect it without invocation — record only its presence.
  const realtimeSubscriptions = [];
  if (realtime !== undefined) {
    const dispatchKind = 'applyEvent' in realtime && realtime.applyEvent !== undefined ? 'applyEvent' : 'onSignal' in realtime && realtime.onSignal !== undefined ? 'onSignal' : 'pure-invalidate';
    for (const subj of realtime.domainPins) {
      realtimeSubscriptions.push({
        subject: subj,
        recipe: dispatchKind
      });
    }
    if (typeof realtime.perEntitySubjects === 'function') {
      realtimeSubscriptions.push({
        subject: `<perEntitySubjects(args)> · rowKey=${String(realtime.rowKey ?? '?')}`,
        recipe: dispatchKind
      });
    }
  }
  // DCE-gated: bundlers replace `process.env.NODE_ENV` with the build
  // constant and fold the branch out of production output entirely.
  if (process.env.NODE_ENV !== 'production') {
    registerQuerySpec(spec, realtimeSubscriptions);
  }
  return function useHook(args, opts) {
    // Render telemetry — gated by a build-time NODE_ENV check so the
    // entire call is DCE-stripped from the production bundle. The
    // condition is build-constant (never per-render) so the
    // rules-of-hooks lint exception is safe — every render across the
    // build lifetime takes the same branch.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useRenderTelemetryDev(spec.specName);
    }
    // (a) Kind-discriminator switch: detail | list | infinite.
    const factory = spec.entityKeys[spec.kind];
    if (factory === undefined) {
      throw new Error(`createHook: spec.entityKeys.${spec.kind} is undefined for ${spec.specName} ` + '(codegen invariant violation — every emitted HookSpec MUST populate the ' + 'factory matching its `kind`).');
    }
    const factoryQueryOpts = factory(args);
    // Both static and realtime hooks already require the QueryClient provider.
    // Resolve it before the branch so hook order stays explicit and invariant.
    const queryClient = useQueryClient();
    const execution = useContext(QueryExecutionContext);
    const viewerScope = useSdkViewerScope();
    const networkScope = useSdkNetworkScope();
    const resolvedEntityQueryKey = resolveEntityQueryKey(spec, args, viewerScope);
    const resolvedCanonicalId = spec.canonicalEntity === true && realtime?.rowKey !== undefined ? String(args[realtime.rowKey] ?? '') : '';
    useConvictionProjectionInterest(queryClient, spec.specName === 'useGetMarket' && opts?.enabled !== false ? resolvedCanonicalId : undefined);
    const canonicalQueryOpts = spec.canonicalEntity === true && resolvedEntityQueryKey !== undefined && resolvedCanonicalId.length > 0 ? {
      ...factoryQueryOpts,
      queryKey: resolvedEntityQueryKey(resolvedCanonicalId)
    } : factoryQueryOpts;
    const queryOpts = execution === null ? canonicalQueryOpts : {
      ...canonicalQueryOpts,
      ...execution(canonicalQueryOpts.queryFn, canonicalQueryOpts.meta)
    };
    const perEntitySubjects = realtime?.perEntitySubjects(args);
    const rowSubjectTemplates = realtime?.rowSubjectTemplates?.(args);
    // A generated hook can have subscriptions for only some argument combinations.
    const pollingHasNats = spec.polling?.hasNats === true && (perEntitySubjects?.some(isPollingSubject) ?? false);
    const pollingManaged = useQueryPolling({
      queryKey: queryOpts.queryKey,
      policy: spec.polling,
      hasNats: pollingHasNats,
      interval: opts?.refetchInterval,
      enabled: opts?.enabled !== false,
      fetchOnce: opts?.fetchOnce,
      batched: extras?.paginatedRefetch !== undefined,
      operationName: spec.specName
    });
    const cachedQuery = opts?.fetchOnce === true ? getCachedQuery(queryClient, queryOpts) : undefined;
    const refetchOnMount = queryOpts.refetchOnMount;
    const reuseExistingFetchOnceSnapshot = opts?.fetchOnce === true && opts?.singleSnapshot !== true && cachedQuery?.state.data !== undefined && typeof refetchOnMount === 'function' && refetchOnMount(cachedQuery) === false;
    // (b) STATIC FALLTHROUGH.
    // FR2 fix #3 — forward the FULL queryOpts bag, not just queryKey +
    // queryFn. Without this, codegen-emitted `staleTime`, `retryOnMount`,
    // function-form `refetchOnMount`, `structuralSharing`, `meta.realtime`
    // are silently discarded → list hooks regress the cold-mount storm.
    if (realtime === undefined) {
      // `opts.enabled === false` short-circuits the fetch — needed by
      // surfaces that gate on a runtime condition (e.g. NSFW moderation
      // polling only while a sha256 is known). `true` / `undefined`
      // preserve the codegen default.
      const mergedOpts = {
        ...queryOpts,
        queryFn: withRestResponseBoundary(ctx => observePollingFetch(queryClient, ctx.queryKey, () => Promise.resolve(queryOpts.queryFn(ctx)), false, ctx.signal), extras?.responseBoundary, {
          args,
          networkScope,
          queryClient,
          queryKey: queryOpts.queryKey,
          viewerScope
        }),
        ...(opts?.enabled === false ? {
          enabled: false
        } : {}),
        ...(pollingManaged ? {
          refetchInterval: false
        } : {}),
        ...(opts?.retry === false ? {
          retry: false
        } : {}),
        ...(opts?.fetchOnce === true ? {
          staleTime: Number.POSITIVE_INFINITY,
          refetchOnMount: false,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
          retry: false
        } : {})
      };
      return useQuery(mergedOpts);
    }
    // (c) REALTIME PATH.
    // Outer-curry: invoke `realtime.applyEvent(args)` / `realtime.onSignal(args)`
    // once per render to produce the inner per-event closure. The wrapper's
    // `applyEventRef` / `onSignalRef` stashes the result; updated args
    // yield updated closures without resubscribing on the registry.
    // The XOR union on `RealtimeSpec` ensures only ONE of the two is
    // populated on a given spec — the runtime checks below mirror that.
    const applyEventInner = 'applyEvent' in realtime && realtime.applyEvent !== undefined ? realtime.applyEvent(args) : undefined;
    const onSignalInner = 'onSignal' in realtime && realtime.onSignal !== undefined ? realtime.onSignal(args) : undefined;
    // `onEvent` (codegen-emitted from `prepend:` recipes) needs
    // `queryClient` access for its post-fetch setQueryData. Curry it
    // here so the inner per-event closure has the live qc bound.
    const qcForOnEvent = terminalAwareQueryCache(queryClient);
    const normalizedEntityKey = resolvedEntityQueryKey;
    const normalizedRowKey = realtime.rowKey;
    const onEventQueryClient = spec.normalized === true && normalizedEntityKey !== undefined ? makeNormalizedOnEventQueryClient(queryClient, queryOpts.queryKey, normalizedEntityKey, normalizedRowKey, queryClient, spec.viewerOverlay, args, viewerScope, spec.entityProjection, spec.nestedEntityProjection, networkScope, spec.entityGcTimeMs) : qcForOnEvent;
    const onEventInner = 'onEvent' in realtime && realtime.onEvent !== undefined ? realtime.onEvent(args, onEventQueryClient) : undefined;
    // Pagination-aware refetch (list hooks only — `extras.paginatedRefetch`).
    // The spec `queryFn` fetches page 1 only; `fetchNextPage` appends
    // pages 2..N via `setQueryData`. A plain parent `refetch()`
    // (reconnect catch-up, subject reacquire after a >GRACE_MS blur —
    // which happens DURING scroll as off-screen rows recycle — or
    // pull-to-refresh) would re-run that page-1 queryFn and REPLACE the
    // whole envelope, truncating an N-page list back to `pageSize`
    // rows. A user scrolled past row `pageSize` is then scrolled into
    // empty space → blank/white list. This wrapper restores the
    // previously-loaded extent: re-fetch page 1, then walk subsequent
    // pages (deduped by rowKey) until at least as many rows as were
    // cached have been re-fetched, rebuilding the full envelope — the
    // same guarantee `useInfiniteQuery.refetch()` gives. Cold load (no
    // prior rows) short-circuits after page 1.
    const baseQueryFn = queryOpts.queryFn;
    const pr = extras?.paginatedRefetch;
    const effectiveQueryFn = pr === undefined || opts?.resetPaginationOnRefetch === true ? baseQueryFn : async ctx => {
      const batchedPoll = consumeBatchedListPoll(queryClient, queryOpts.queryKey);
      const prior = qcForOnEvent.getQueryData(queryOpts.queryKey);
      const priorCount = Array.isArray(prior?.data) ? prior.data.length : 0;
      if (batchedPoll) {
        const first = filterTerminalRows(queryClient, queryOpts.queryKey, await pr.fetchPage(args, null, {
          limit: Math.min(pr.maxPageSize, Math.max(Number(args[pr.limitParam] ?? pr.pageSize), priorCount)),
          signal: ctx.signal
        }));
        // Pages appended while page 1 was in flight belong to the loaded
        // extent too — read the cache again now, not only at start.
        let targetCount = Math.max(priorCount, cachedRowCount(qcForOnEvent, queryOpts.queryKey));
        const seen = new Set();
        const acc = [];
        for (const row of first.data) {
          const key = String(row[pr.rowKey]);
          if (seen.has(key)) continue;
          seen.add(key);
          acc.push(row);
        }
        let latest = first;
        let cursor = first.nextCursor;
        let hasMore = first.hasMore;
        while (cursor !== null) {
          targetCount = Math.min(targetCount, cachedRowCount(qcForOnEvent, queryOpts.queryKey));
          if (acc.length >= targetCount) break;
          const page = filterTerminalRows(queryClient, queryOpts.queryKey, await pr.fetchPage(args, cursor, {
            limit: Math.min(pr.maxPageSize, targetCount - acc.length),
            signal: ctx.signal
          }));
          const before = acc.length;
          for (const row of page.data) {
            const key = String(row[pr.rowKey]);
            if (seen.has(key)) continue;
            seen.add(key);
            acc.push(row);
          }
          latest = page;
          cursor = page.nextCursor;
          hasMore = page.hasMore;
          if (acc.length === before) break;
        }
        return {
          ...latest,
          data: acc,
          nextCursor: cursor,
          hasMore
        };
      }
      const first = filterTerminalRows(queryClient, queryOpts.queryKey, await baseQueryFn(ctx));
      // A scroll-driven append can land while page 1 is in flight; the
      // extent to restore is whatever the cache holds now.
      const loadedCount = Math.max(priorCount, cachedRowCount(qcForOnEvent, queryOpts.queryKey));
      // Cold load or list shrank — page 1 is the whole truth.
      if (loadedCount <= first.data.length) return first;
      const seen = new Set();
      const acc = [];
      for (const r of first.data) {
        const k = String(r[pr.rowKey]);
        if (!seen.has(k)) {
          seen.add(k);
          acc.push(r);
        }
      }
      let latest = first;
      let cursor = first.nextCursor;
      let hasMore = first.hasMore;
      let targetCount = loadedCount;
      // Restore the loaded extent. Bounded: stops at the prior
      // row count, when the cursor runs out, or if a page yields
      // no new rows (no-progress guard for a pathological cursor).
      while (cursor !== null) {
        targetCount = Math.min(targetCount, cachedRowCount(qcForOnEvent, queryOpts.queryKey));
        if (acc.length >= targetCount) break;
        const pg = filterTerminalRows(queryClient, queryOpts.queryKey, await pr.fetchPage(args, cursor, {
          signal: ctx.signal
        }));
        const before = acc.length;
        for (const r of pg.data) {
          const k = String(r[pr.rowKey]);
          if (!seen.has(k)) {
            seen.add(k);
            acc.push(r);
          }
        }
        latest = pg;
        cursor = pg.nextCursor;
        hasMore = pg.hasMore;
        if (acc.length === before) break;
      }
      return {
        ...latest,
        data: acc,
        nextCursor: cursor,
        hasMore
      };
    };
    const responseQueryFn = withRestResponseBoundary(async ctx => {
      const response = await effectiveQueryFn(ctx);
      if (pollingManaged) capturePollingResponse(queryClient, ctx.queryKey, response);
      return response;
    }, extras?.responseBoundary, {
      args,
      networkScope,
      queryClient,
      queryKey: queryOpts.queryKey,
      viewerScope
    });
    // MOR-859 · normalized entity split. For a `normalized: true` list hook,
    // wrap the fetch so every returned row is upserted into its per-entity
    // cache entry (`spec.entityQueryKey(id)`) and the feed cache stores only
    // the ordered id list. `rowKey` comes from the realtime spec. The wrapper
    // is inert on every other hook (`spec.normalized` absent).
    const normalizedRowSubjectTemplates = realtime.rowSubjectTemplates?.(args) ?? [];
    const canonicalEntityId = spec.canonicalEntity === true && normalizedRowKey !== undefined ? String(args[normalizedRowKey] ?? '') : '';
    const finalQueryFn = spec.giftEntity === true && responseQueryFn !== undefined ? async ctx => {
      const observedAtMs = Date.now();
      const response = await responseQueryFn(ctx);
      writeGiftEntityResponse(queryClient, response, {
        network: networkScope,
        viewerScope
      }, {
        source: 'rest',
        observedAtMs
      });
      return response;
    } : spec.publicUserEntity === true && responseQueryFn !== undefined ? async ctx => {
      const observedAtMs = Date.now();
      const row = await responseQueryFn(ctx);
      if (row !== null && typeof row === 'object') {
        const identifier = args.identifier;
        upsertPublicUserEntity(queryClient, row, {
          source: 'rest',
          observedAtMs,
          aliases: typeof identifier === 'string' ? [identifier] : []
        });
      }
      return row !== null && typeof row === 'object' ? stripPublicUserIdentityFields(row) : row;
    } : spec.normalized === true && normalizedEntityKey !== undefined && normalizedRowKey !== undefined && responseQueryFn !== undefined ? async ctx => {
      const observedAtMs = Date.now();
      const env = await responseQueryFn(ctx);
      return splitRowsIntoEntities(queryClient, env, normalizedEntityKey, normalizedRowKey, {
        source: 'rest',
        observedAtMs
      }, spec.viewerOverlay, args, viewerScope, spec.entityProjection, spec.nestedEntityProjection, spec.entityGcTimeMs, ctx.queryKey);
    } : spec.canonicalEntity === true && canonicalEntityId.length > 0 && responseQueryFn !== undefined ? async ctx => {
      const observedAtMs = Date.now();
      const row = await responseQueryFn(ctx);
      if (row === null || typeof row !== 'object') return row;
      const global = normalizedEntityKey === undefined ? row : splitViewerOverlayRow(queryClient, row, canonicalEntityId, normalizedEntityKey, spec.viewerOverlay, args, viewerScope, {
        source: 'rest',
        observedAtMs
      });
      const snapshot = splitNestedEntityProjection(queryClient, global, spec.nestedEntityProjection, {
        source: 'rest',
        observedAtMs
      });
      if (normalizedEntityKey === undefined) return snapshot;
      writeNormalizedEntitySnapshot(queryClient, normalizedEntityKey(canonicalEntityId), snapshot, {
        source: 'rest',
        observedAtMs
      });
      return queryClient.getQueryData(normalizedEntityKey(canonicalEntityId)) ?? snapshot;
    } : responseQueryFn;
    // MOR-859 · normalized realtime. The feed cache holds only the id list, so
    // wrap the generated per-row reducer to reconstruct rows from the entity
    // store, patch the changed entity once (shared across every feed), and
    // return the re-ranked id list. Inert on non-normalized hooks.
    const finalApplyEvent = spec.giftEntity === true && applyEventInner !== undefined ? makeGiftEntityApplyEvent(applyEventInner, queryClient, {
      network: networkScope,
      viewerScope
    }) : spec.normalized === true && normalizedEntityKey !== undefined && normalizedRowKey !== undefined && applyEventInner !== undefined ? makeNormalizedApplyEvent(applyEventInner, realtime.rerank, queryClient, normalizedEntityKey, normalizedRowKey, normalizedRowSubjectTemplates, queryOpts.queryKey, spec.entityProjection, spec.nestedEntityProjection, spec.viewerOverlay, args, viewerScope, networkScope, spec.rerankSiblingFeeds !== false, spec.entityGcTimeMs) : spec.canonicalEntity === true && applyEventInner !== undefined ? makeCanonicalEntityApplyEvent(applyEventInner, queryClient, String(args[normalizedRowKey]), normalizedEntityKey, spec.viewerOverlay, args, viewerScope, spec.nestedEntityProjection) : applyEventInner;
    // T16001 · perEntitySubjects routed through structural-equality memo
    // (spec §431). We forward both the user's `perEntitySubjects` fn AND
    // the args to `useRealtimeQuery`, which keys a single-slot ref-based
    // memo on `JSON.stringify(args)` via `useStableEntitySubjects`. The
    // subjects resolved above are forwarded as the
    // initial value so existing wrapper tests (which mock
    // `useRealtimeQuery`) continue to see the array form directly.
    const queryMeta = queryOpts.meta;
    const realtimeMeta = opts?.resetPaginationOnRefetch === true ? {
      ...queryMeta,
      reconcileTrailing: true
    } : queryMeta;
    const result = useRealtimeQuery({
      pollingManaged,
      pollingHasNats,
      queryKey: queryOpts.queryKey,
      queryFn: finalQueryFn,
      ...(realtimeMeta !== undefined ? {
        meta: realtimeMeta
      } : {}),
      ...(queryOpts.structuralSharing === undefined ? {} : {
        structuralSharing: queryOpts.structuralSharing
      }),
      ...(queryOpts.refetchOnMount === 'always' ? {
        authoritativeRefetchOnMount: true
      } : {}),
      ...(queryOpts.refetchOnWindowFocus === 'always' ? {
        authoritativeRefetchOnWindowFocus: true
      } : {}),
      domain: realtime.domain,
      ...(realtime.responseSubjects === undefined ? {} : {
        responseSubjects: realtime.responseSubjects
      }),
      domainPins: realtime.domainPins,
      perEntitySubjects: perEntitySubjects ?? [],
      // The wrapper accepts `unknown`-keyed fns because it stores the
      // function on a ref and re-invokes it through a value-equal
      // `JSON.stringify(args)` slot. `Args extends JsonSerialisableArgs`
      // is invariant w.r.t. function-arg position, but the runtime
      // contract is "feed it the same args you registered with" — the
      // type-level cast captures that.
      perEntitySubjectsFn: realtime.perEntitySubjects,
      perEntitySubjectsArgs: args,
      // Per-row subject templates (`morfi.market.{rowKey}.…`) — the SDK
      // substitutes a row id into the `{…}` placeholder. `useRealtimeQuery`
      // uses them to extract the row id from a concrete event subject and
      // gate dispatch per-row by viewport visibility. Absent on hooks with
      // no per-row realtime → the gate stays inert.
      ...(rowSubjectTemplates !== undefined ? {
        rowSubjectTemplates
      } : {}),
      // Fan-out subjects (array args) are viewport-gated per entity by the
      // registry; the call site may remap a subject to the row that gates it.
      ...(realtime.gatedSubjects !== undefined ? {
        gatedSubjects: realtime.gatedSubjects(args)
      } : {}),
      ...(opts?.subjectGateId !== undefined ? {
        subjectGateId: opts.subjectGateId
      } : {}),
      ...(realtime.isTerminalEvent !== undefined ? {
        isTerminalEvent: realtime.isTerminalEvent(args)
      } : {}),
      // Per-QUERY viewport gate — for an arg-bound list hook (`useGetTrades`,
      // `useGetMarketTraders`) the whole query belongs to one entity named
      // by `viewportEntityArg`. Resolve `args[that arg]` and thread it as
      // `viewportEntity`; `useRealtimeQuery` buffers the query's dispatch
      // whenever that entity's card is offscreen. A non-string / missing
      // arg (e.g. used on a page that doesn't pass it) → no gate. The
      // call-site opt-in is required: a detail query can share the same
      // entity id as an offscreen feed card and must not inherit that card's
      // visibility state.
      ...(realtime.viewportEntityArg !== undefined && args.viewportGated === true && typeof args[realtime.viewportEntityArg] === 'string' ? {
        viewportEntity: args[realtime.viewportEntityArg]
      } : {}),
      // Per-call-site override of the spec's `viewportGated` default.
      // Consumers may pass `viewportGated: true` on any `useGet*` arg
      // to opt in to per-element gating; codegen does not need to
      // duplicate this propagation per hook.
      viewportGated: args.viewportGated ?? realtime.viewportGated,
      // `opts.enabled === false` from the call site also disables the
      // realtime path's underlying useQuery and registry acquisitions —
      // mirrors the static branch above while keeping React hook order
      // stable. Pass through via useRealtimeQuery's `enabled`.
      ...(opts?.enabled === false ? {
        enabled: false
      } : {}),
      ...(opts?.fetchOnce === true ? {
        fetchOnce: true,
        refetchInterval: false,
        reuseExistingFetchOnceSnapshot
      } : pollingManaged ? {
        refetchInterval: false
      } : {}),
      // The eager default lives in useRealtimeQuery; forward only the opt-out.
      ...(opts?.eagerSnapshot === false ? {
        eagerSnapshot: false
      } : {}),
      ...(opts?.singleSnapshot === true ? {
        singleSnapshot: true
      } : {}),
      ...(opts?.retry === false ? {
        retry: false
      } : {}),
      specName: spec.specName,
      ...(realtime.applyImmediately === true ? {
        applyImmediately: true
      } : {}),
      ...(realtime.admitSequenceOnlyOnMutation === true ? {
        admitSequenceOnlyOnMutation: true
      } : {}),
      ...(realtime.sequenceGate === false ? {
        sequenceGate: false
      } : {}),
      ...(realtime.reconcileOnStaleSequence === true ? {
        reconcileOnStaleSequence: true
      } : {}),
      ...(realtime.requiresAuthoritativeRefetch === undefined ? {} : {
        requiresAuthoritativeRefetch: realtime.requiresAuthoritativeRefetch(args)
      }),
      ...(realtime.applyBeforeAuthoritativeRefetch === true ? {
        applyBeforeAuthoritativeRefetch: true
      } : {}),
      ...(realtime.authoritativeRefetchMaxAttempts === undefined ? {} : {
        authoritativeRefetchMaxAttempts: realtime.authoritativeRefetchMaxAttempts
      }),
      ...(realtime.sequenceGateSubject !== undefined ? {
        sequenceGateSubject: realtime.sequenceGateSubject(args)
      } : {}),
      // Every indexer-published realtime proto carries the canonical chain
      // cursor in field 1. Generated hooks fail closed on missing/malformed
      // cursors so an unsequenced payload can never mutate a REST snapshot.
      requireEventSequence: true,
      // Forward the realtime spec's `rowKey` (e.g. `'signature'`, `'pubkey'`).
      // Drives `useRealtimeQuery`'s diff-based `tagLiveRow` at every applyEvent
      // call site — newly-prepended rowKeys feed the per-hook-instance
      // `liveSinceMountIds` projection on list-hook results. Absent for
      // single-row hooks; the diff branch stays inert.
      ...(realtime.rowKey !== undefined ? {
        rowKey: realtime.rowKey
      } : {}),
      // Forward the spec's `reconcileOnInsert` flag (set by codegen for an
      // event-as-row `prepend: { reconcile: true }`). When set, the runtime
      // fires one throttled REST refetch after realtime inserts a new row,
      // reconciling the partial placeholder to the authoritative server row.
      ...(realtime.reconcileOnInsert === true ? {
        reconcileOnInsert: true
      } : {}),
      // Forward the spec's `reconcileAlwaysSubjects` (concrete arg-substituted
      // subjects — the viewer's own market-scoped wallet trade subjects). When an
      // incoming event subject matches one, the runtime fires ONE throttled
      // REST refetch regardless of list growth, so a view-only client relearns
      // structural state (balance / cost basis) the wire omits on the viewer's
      // OWN rebuy/sell. See MOR-923.
      ...(typeof realtime.reconcileAlwaysSubjects === 'function' ? {
        reconcileAlwaysSubjects: realtime.reconcileAlwaysSubjects(args)
      } : {}),
      ...(finalApplyEvent !== undefined ? {
        applyEvent: finalApplyEvent
      } : {}),
      ...(onSignalInner !== undefined ? {
        onSignal: onSignalInner
      } : {}),
      ...(onEventInner !== undefined ? {
        onEvent: onEventInner
      } : {}),
      // Forward the spec's `onReacquire` recipe (default refetch /
      // 'none' / function) + the args so the runtime layer can call it
      // with the right closure. The args bag is passed straight through
      // because the spec may need queryKey-shaped fields from it.
      // Cast: RealtimeSpec.onReacquire is generic over Args; useRealtimeQuery
      // takes `unknown` (it doesn't know the spec's Args type). Same runtime
      // bag flows through, just type-erased at the boundary.
      ...(opts?.fetchOnce === true ? {
        onReacquire: 'none'
      } : 'onReacquire' in realtime && realtime.onReacquire !== undefined ? {
        onReacquire: realtime.onReacquire,
        onReacquireArgs: args
      } : {}),
      // Page-level `select` (docs §Render Optimizations · select). The
      // list-hook factory passes a constant-returning select so the
      // consumer-facing observer's data never changes value, suppressing
      // re-renders triggered by cache patches; the cache itself still holds
      // the real data for sibling observers (Items, Field) to read.
      ...(extras?.select !== undefined ? {
        select: extras.select
      } : {})
    });
    const queryKeyHash = hashKey(queryOpts.queryKey);
    useEffect(() => {
      if (spec.giftEntity !== true) return;
      const sourceQuery = queryClient.getQueryCache().get(queryKeyHash);
      writeGiftEntityResponse(queryClient, sourceQuery?.state.data, {
        network: networkScope,
        viewerScope
      }, {
        source: 'hydrate',
        observedAtMs: 0
      });
    }, [networkScope, queryClient, queryKeyHash, viewerScope]);
    useEffect(() => {
      if (spec.canonicalEntity !== true || spec.nestedEntityProjection === undefined) return;
      const current = queryClient.getQueryData(queryOpts.queryKey);
      if (!hasNestedEntitySnapshots(current, spec.nestedEntityProjection)) return;
      const split = splitNestedEntityProjection(queryClient, current, spec.nestedEntityProjection, {
        source: 'hydrate',
        observedAtMs: 0
      });
      queryClient.setQueryData(queryOpts.queryKey, split);
    }, [queryClient, queryOpts.queryKey]);
    const nestedProjection = spec.nestedEntityProjection;
    const nestedProjectionRef = useRef(nestedProjection);
    const nestedSpecNameRef = useRef(spec.specName);
    nestedProjectionRef.current = nestedProjection;
    nestedSpecNameRef.current = spec.specName;
    const nestedIds = spec.canonicalEntity === true ? nestedEntityIds(result.data, nestedProjection) : [];
    const nestedQueries = useQueries({
      queries: nestedProjection === undefined ? [] : nestedIds.map(id => {
        const queryKey = projectionEntityQueryKey(nestedProjection, id);
        return {
          queryKey,
          queryFn: createSubscriberQueryFn(queryClient, queryKey),
          enabled: false,
          staleTime: Number.POSITIVE_INFINITY,
          // An explicitly isolated owner uses this observer only to
          // guard against a missing normalized row. Selecting the stable
          // id leaves value patches in the field hooks that consume them.
          ...(opts?.isolateNestedEntityRenders === true ? {
            select: () => id
          } : {})
        };
      })
    });
    const missingNestedKey = nestedIds.filter((_nestedId, index) => nestedQueries[index]?.data === undefined).join('|');
    const recoveredNestedRef = useRef(null);
    useEffect(() => {
      const projection = nestedProjectionRef.current;
      if (projection === undefined || missingNestedKey.length === 0) {
        recoveredNestedRef.current = null;
        return;
      }
      const recoveringIds = missingNestedKey.split('|');
      if (recoveringIds.every(nestedId => queryClient.getQueryData(projectionEntityQueryKey(projection, nestedId)) !== undefined)) {
        recoveredNestedRef.current = null;
        return;
      }
      if (opts?.fetchOnce === true || hasActiveFetchOnceRealtimeOwner(queryClient, queryOpts.queryKey)) {
        recoveredNestedRef.current = missingNestedKey;
        if (projection.resource === 'outcomes') {
          recordOutcomeEntityDiagnostic({
            kind: 'missing-entity-recovery-failure',
            entityHash: hashDiagnosticEntity(missingNestedKey),
            source: nestedSpecNameRef.current
          });
        }
        return;
      }
      if (queryClient.getQueryState(queryOpts.queryKey)?.fetchStatus === 'fetching') return;
      if (recoveredNestedRef.current === missingNestedKey) return;
      recoveredNestedRef.current = missingNestedKey;
      void refetchRealtimeQuery(queryClient, queryOpts.queryKey).then(outcome => {
        const stillMissing = recoveringIds.some(nestedId => queryClient.getQueryData(projectionEntityQueryKey(projection, nestedId)) === undefined);
        if (projection.resource === 'outcomes' && (outcome !== 'success' || stillMissing)) {
          recordOutcomeEntityDiagnostic({
            kind: 'missing-entity-recovery-failure',
            entityHash: hashDiagnosticEntity(missingNestedKey),
            source: nestedSpecNameRef.current
          });
        }
      });
    }, [missingNestedKey, opts?.fetchOnce, queryClient, queryOpts.queryKey]);
    const overlayKeys = spec.canonicalEntity === true && normalizedEntityKey !== undefined && canonicalEntityId.length > 0 ? viewerOverlaySubscriptionKeys(normalizedEntityKey, canonicalEntityId, spec.viewerOverlay, args, viewerScope) : [];
    useQueries({
      queries: overlayKeys.map(queryKey => ({
        queryKey,
        queryFn: createSubscriberQueryFn(queryClient, queryKey),
        enabled: false,
        staleTime: Number.POSITIVE_INFINITY
      }))
    });
    const overlayRecoveryKey = overlayKeys.map(queryKey => hashKey(queryKey)).join('|');
    const recoveredOverlayRef = useRef(null);
    useEffect(() => {
      if (spec.canonicalEntity !== true || spec.viewerOverlay === undefined || normalizedEntityKey === undefined || canonicalEntityId.length === 0) {
        return;
      }
      const current = queryClient.getQueryData(queryOpts.queryKey);
      if (current === null || typeof current !== 'object') return;
      const global = splitViewerOverlayRow(queryClient, current, canonicalEntityId, normalizedEntityKey, spec.viewerOverlay, args, viewerScope, {
        source: 'hydrate',
        observedAtMs: 0
      });
      if (global !== current) queryClient.setQueryData(queryOpts.queryKey, global);
      const missingOverlay = overlayKeys.some(overlayKey => queryClient.getQueryData(overlayKey) === undefined);
      if (!missingOverlay) {
        recoveredOverlayRef.current = null;
        return;
      }
      if (queryClient.getQueryState(queryOpts.queryKey)?.fetchStatus === 'fetching') return;
      if (recoveredOverlayRef.current === overlayRecoveryKey) return;
      recoveredOverlayRef.current = overlayRecoveryKey;
      recordViewerOverlayRecovery(canonicalEntityId);
      void refetchRealtimeQuery(queryClient, queryOpts.queryKey);
    }, [queryClient, canonicalEntityId, viewerScope, args, queryOpts.queryKey, overlayKeys, overlayRecoveryKey, normalizedEntityKey]);
    const hasViewerProjection = spec.canonicalEntity === true && spec.viewerOverlay !== undefined && normalizedEntityKey !== undefined && canonicalEntityId.length > 0;
    const hasNestedProjection = spec.canonicalEntity === true && spec.nestedEntityProjection !== undefined;
    if (!hasViewerProjection && !hasNestedProjection) {
      return result;
    }
    return new Proxy(result, {
      get(target, property, receiver) {
        if (property !== 'data') return Reflect.get(target, property, receiver);
        const data = Reflect.get(target, property, receiver);
        if (data === undefined) return undefined;
        const global = hasViewerProjection && normalizedEntityKey !== undefined ? mergeViewerOverlayRow(queryClient, data, canonicalEntityId, normalizedEntityKey, spec.viewerOverlay, args, viewerScope) : data;
        return materializeNestedEntityProjection(queryClient, global, spec.nestedEntityProjection);
      }
    });
  };
}
function withRestResponseBoundary(queryFn, boundary, context) {
  if (boundary === undefined) return queryFn;
  return async queryContext => {
    const observedAtMs = Date.now();
    const viewerAccountId = getSdkAuthStore().current().user?.id ?? null;
    const data = await queryFn(queryContext);
    return boundary(data, {
      ...context,
      observedAtMs,
      viewerAccountId
    });
  };
}
function cachedRowCount(cache, queryKey) {
  const cached = cache.getQueryData(queryKey);
  return Array.isArray(cached?.data) ? cached.data.length : 0;
}